import { useTranslation } from 'react-i18next';

import { IMAGE_CONSTANTS } from '@/constants';

import { formatElapsedTime } from './ElapsedTimer';

import type { TaskDetail } from '@/types';

interface InProgressBottomSheetProps {
  task: TaskDetail;
  elapsedSeconds?: number;
  onViewAction: () => void;
  onComplete: () => void;
}

/**
 * In-progress task indicator bar — matches Xamarin DashboardPage inprogressTask Frame.
 *
 * Xamarin layout (DashboardPage.xaml lines 219-276):
 *  - Frame: bg=#CF365A (InprogressTaskBackgroundColor), CornerRadius=10, Margin="10,5"
 *  - HeightRequest: Phone=60, Tablet=75
 *  - Grid columns:
 *    Column 0 (80-100px): Colored bg section (Move=#E38C25, Audit=#273F94) with icon + TaskType label
 *    Column 1 (*): Task description text
 *    Column 2 (Auto): Timer icon + elapsed time
 *    Column 3 (80-100px): Complete button
 */
export function InProgressBottomSheet({
  task,
  elapsedSeconds = 0,
  onViewAction,
  onComplete,
}: InProgressBottomSheetProps) {
  const { t } = useTranslation();
  const elapsed = elapsedSeconds;

  const isMove = task.TaskType === 'Move';
  const taskTypeIcon = isMove ? IMAGE_CONSTANTS.moveTask : IMAGE_CONSTANTS.auditTask;
  // Xamarin: MoveTaskBackgroundColor=#E38C25, AuditTaskBackgroundColor=#273F94
  const taskTypeBg = isMove ? '#E38C25' : '#273F94';

  // Xamarin CarrierDetailsConverter: " | CarrierCompanyName - CarrierName" when CarrierName is non-empty
  const carrierDetails = task.CarrierName.trim()
    ? ` | ${task.CarrierCompanyName} - ${task.CarrierName}`
    : '';

  return (
    <div className="fixed bottom-[72px] left-0 right-0 z-40 px-2 py-[5px]">
      {/* Xamarin: Frame bg=#CF365A, CornerRadius=10, Margin="10,5", HeightRequest=60/75 */}
      <div
        className="flex h-[90px] overflow-hidden rounded-[10px]"
        style={{ backgroundColor: '#CF365A' }}
      >
        {/* ── Column 0: Colored task type section ──
            Xamarin DashboardPage.xaml lines 235-276:
            StackLayout with BackgroundColor=MoveTaskBackgroundColor/AuditTaskBackgroundColor
            Contains icon (30px) + TaskType label, Width=80-100px
            self-stretch fills the full bar height with no gap */}
        <div
          className="flex w-[80px] shrink-0 flex-col items-center justify-center self-stretch"
          style={{ backgroundColor: taskTypeBg }}
        >
          <img src={taskTypeIcon} alt={task.TaskType} className="h-[30px] w-[30px]" />
          <span className="mt-0.5 text-2xl text-white font-poppins">{task.TaskType}</span>
        </div>

        {/* ── Column 1 (*): Task description — vertically centered ──
            Xamarin DashboardPage.xaml lines 278-386: FormattedString with full task description
            Move: "Move Task : The trailer {TrailerName} from {Source} to {Dest} | Carrier"
            Audit+trailer: "Audit Task : Check if trailer {TrailerName} in {Location} | Carrier"
            Audit-trailer: "Audit Task : Check if {Location} is empty or not." */}
        <button
          type="button"
          className="flex flex-1 items-center min-w-0 px-3"
          onClick={onViewAction}
        >
          <span className="text-white font-poppins text-[28px] leading-tight line-clamp-2 text-left">
            {isMove ? (
              <>
                <span className="font-normal">{t('dashboard.moveDescription')} </span>
                <span className="font-semibold">{task.TrailerName}</span>
                <span className="font-normal"> {t('dashboard.from')} </span>
                <span className="font-semibold">{task.MoveSourceName}</span>
                <span className="font-normal"> {t('dashboard.to')} </span>
                <span className="font-semibold">{task.MoveDestinationName}</span>
                {carrierDetails && <span className="font-normal">{carrierDetails}</span>}
              </>
            ) : task.TrailerName ? (
              <>
                <span className="font-normal">{t('dashboard.auditDescriptionWithTrailer')}</span>
                <span className="font-semibold">{task.TrailerName}</span>
                <span className="font-normal"> {t('dashboard.in')} </span>
                <span className="font-semibold">{task.AuditLocationName}</span>
                {carrierDetails && <span className="font-normal">{carrierDetails}</span>}
              </>
            ) : (
              <>
                <span className="font-normal">{t('dashboard.auditDescriptionWithoutTrailer')}</span>
                <span className="font-semibold">{task.AuditLocationName}</span>
                <span className="font-normal">{t('dashboard.emptyOrNot')}</span>
              </>
            )}
          </span>
        </button>

        {/* ── Column 2 (Auto): Timer — vertically centered ── */}
        <div className="flex flex-col items-center justify-center mx-2 shrink-0">
          <img src={IMAGE_CONSTANTS.inprogressTimerMobile} alt="Timer" className="h-5 w-5" />
          <span className="text-white text-xs font-poppins">{formatElapsedTime(elapsed)}</span>
        </div>

        {/* ── Column 3 (Auto): Complete — vertically centered ── */}
        <button
          type="button"
          className="flex flex-col items-center justify-center mx-2 shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onComplete();
          }}
        >
          <img src={IMAGE_CONSTANTS.completeTask} alt="Complete" className="h-5 w-5" />
          <span className="text-white text-xs font-poppins">{t('common.complete')}</span>
        </button>
      </div>
    </div>
  );
}
